import type { ReactElement } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { isEqual, noop, pull } from 'lodash';
import { forceCheck } from 'react-lazyload';
type Props = {
  activeIndexes?: Array<number>;
  children: (arg0: {
    activeIndexes: Array<number>;
    clear: () => void;
    isPristine: boolean;
    lastIndex: number | null | undefined;
    update: (index: number) => void;
  }) => ReactElement;
  hasMultipleIndexEnabled?: boolean;
  hasNoIndexEnabled?: boolean;
  onUpdate?: (index: number) => void;
};

export function ActiveIndexes({
  activeIndexes: activeIndexesProp = [],
  children,
  hasMultipleIndexEnabled = false,
  hasNoIndexEnabled = false,
  onUpdate = noop,
}: Props) {
  const [activeIndexes, setActiveIndexes] = useState<Array<number>>(activeIndexesProp);
  const [isPristine, setIsPristine] = useState(true);
  const [lastIndex, setLastIndex] = useState(0);

  const prevActiveIndexesProp = useRef(activeIndexesProp);

  useEffect(() => {
    if (!isEqual(prevActiveIndexesProp.current, activeIndexesProp)) {
      const newLastIndex = activeIndexesProp[activeIndexesProp.length - 1];
      setActiveIndexes(activeIndexesProp);
      if (newLastIndex >= 0) {
        setLastIndex(newLastIndex);
      }
    }
    prevActiveIndexesProp.current = activeIndexesProp;
  }, [activeIndexesProp]);

  const clear = useCallback(() => {
    setActiveIndexes((prev) => {
      if (prev.length) {
        return [];
      }
      return prev;
    });
  }, []);

  const update = useCallback(
    (index: number) => {
      setActiveIndexes((prev) => {
        const isIncluded = prev.includes(index);
        let newActiveIndexes: Array<number>;

        if (isIncluded) {
          if (hasNoIndexEnabled || prev.length > 1) {
            newActiveIndexes = pull([...prev], index);
          } else {
            newActiveIndexes = prev;
          }
        } else if (hasMultipleIndexEnabled) {
          newActiveIndexes = [...prev, index];
        } else {
          newActiveIndexes = [index];
        }

        return newActiveIndexes;
      });
      setIsPristine(false);
      setLastIndex(index);
      onUpdate(index);
      forceCheck();
    },
    [hasMultipleIndexEnabled, hasNoIndexEnabled, onUpdate],
  );

  return children({
    activeIndexes,
    clear,
    isPristine,
    lastIndex,
    update,
  });
}
